import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { fetchSkills } from "@/api/fetchSkills/fetchSkillsApi";
import { createSkill } from "@/api/createSkill/createSkillApi";
import { updateSkill } from "@/api/updateSkill/updateSkillApi";
import { deleteSkill } from "@/api/deleteSkill/deleteSkillApi";
import type {
  FetchSkillsParams,
  FetchSkillsResponse,
} from "@/api/fetchSkills/types";
import type {
  CreateSkillPayload,
  CreateSkillResponse,
} from "@/api/createSkill/types";
import type {
  UpdateSkillPayload,
  UpdateSkillResponse,
} from "@/api/updateSkill/types";
import type { SkillWithAssessments } from "@/modules/learningCenter/types/skill";

/**
 * Skill Management State
 *
 * Manages the list of skills with pagination and CRUD operations
 */
export interface SkillManagementState {
  skills: SkillWithAssessments[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
  };
}

// Initial state
const initialState: SkillManagementState = {
  skills: [],
  totalCount: 0,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 20,
  },
};

/**
 * Async thunk: Fetch skills with pagination
 */
export const fetchSkillsThunk = createAsyncThunk<
  FetchSkillsResponse,
  FetchSkillsParams | void,
  { rejectValue: string }
>("skillManagement/fetchSkills", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchSkills(params || undefined);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch skills",
    );
  }
});

/**
 * Async thunk: Create a new skill
 */
export const createSkillThunk = createAsyncThunk<
  CreateSkillResponse,
  CreateSkillPayload,
  { rejectValue: string }
>("skillManagement/createSkill", async (payload, { rejectWithValue }) => {
  try {
    const response = await createSkill(payload);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to create skill",
    );
  }
});

/**
 * Async thunk: Update an existing skill
 */
export const updateSkillThunk = createAsyncThunk<
  UpdateSkillResponse,
  { id: number; payload: UpdateSkillPayload },
  { rejectValue: string }
>(
  "skillManagement/updateSkill",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await updateSkill(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update skill",
      );
    }
  },
);

/**
 * Async thunk: Delete a skill
 */
export const deleteSkillThunk = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("skillManagement/deleteSkill", async (id, { rejectWithValue }) => {
  try {
    await deleteSkill(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete skill",
    );
  }
});

/**
 * Skill Management Slice
 */
const skillManagementSlice = createSlice({
  name: "skillManagement",
  initialState,
  reducers: {
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.pageSize !== undefined) {
        state.pagination.pageSize = action.payload.pageSize;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch skills
    builder
      .addCase(fetchSkillsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSkillsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.skills = action.payload.data.skills;
        state.totalCount = action.payload.meta?.totalCount || 0;
      })
      .addCase(fetchSkillsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch skills";
      });

    // Create skill
    builder
      .addCase(createSkillThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSkillThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new skill to the beginning of the list
        state.skills.unshift(action.payload.data);
        state.totalCount += 1;
      })
      .addCase(createSkillThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create skill";
      });

    // Update skill
    builder
      .addCase(updateSkillThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSkillThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.skills.findIndex(
          (skill) => skill.id === action.payload.data.id,
        );
        if (index !== -1) {
          state.skills[index] = action.payload.data;
        }
      })
      .addCase(updateSkillThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update skill";
      });

    // Delete skill
    builder
      .addCase(deleteSkillThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSkillThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the skill from the list
        state.skills = state.skills.filter(
          (skill) => skill.id !== action.payload,
        );
        state.totalCount -= 1;
      })
      .addCase(deleteSkillThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete skill";
      });
  },
});

// Actions
export const { setPagination, clearError } = skillManagementSlice.actions;

// Selectors
export const selectSkills = (state: RootState) =>
  state.skillManagement?.skills || [];
export const selectTotalCount = (state: RootState) =>
  state.skillManagement?.totalCount || 0;
export const selectLoading = (state: RootState) =>
  state.skillManagement?.loading || false;
export const selectError = (state: RootState) =>
  state.skillManagement?.error || null;
export const selectPagination = (state: RootState) =>
  state.skillManagement?.pagination || initialState.pagination;

// Reducer
export default skillManagementSlice.reducer;
